import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useRouterState, Link } from "@tanstack/react-router";
import { Menu, User, X, Search, ShoppingCart } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { LazyCartDrawer as CartDrawer } from "@/components/LazyCartDrawer";
// Vita assistant is hidden for now — code kept in src/components/Vita.tsx /
// DeferredVita.tsx; re-add <DeferredVita /> below to re-enable.
import { WhatsAppChatButton } from "@/components/WhatsAppChatButton";
// import VoiceAgent from "@/components/VoiceAgent"; — disabled for now, keep the code
import { BottomNav } from "@/components/BottomNav";
import "@/components/site-chrome-refresh.css";
import { SiteFooter } from "@/components/SiteChrome";
import { UrgencyStrip } from "@/components/urgency/UrgencyStrip";
import { InauguralPriceBanner } from "@/components/InauguralPriceBanner";
import { useCartStore } from "@/stores/cartStore";

import truweLogo from "@/assets/opt/truwe-logo-precision.webp";


import { registerPwa } from "@/lib/pwa-register";
import { useT } from "@/lib/i18n/provider";
import type { TranslationKey } from "@/lib/i18n/dictionary";
import { LanguageToggle } from "@/components/i18n/LanguageToggle";
import { DownloadAppButton } from "@/components/notifications/DownloadAppButton";

// Routes that opt out of the app-shell phone frame (admin tools, raw checkout, auth callback).
const BARE_ROUTES = ["/admin", "/checkout", "/order-success"];

export function AppShell() {
  const [cartOpen, setCartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const bare = BARE_ROUTES.some((r) => pathname.startsWith(r));
  const isAccount = pathname.startsWith("/account") || pathname.startsWith("/auth");
  // The admin panel renders its own compact header — no storefront chrome at
  // all (no AppHeader, no menu, no cart drawer).
  const isAdmin = pathname.startsWith("/admin");

  // Close the menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    registerPwa();
  }, []);

  useEffect(() => {
    const openCart = () => setCartOpen(true);
    (window as unknown as { __openCart?: () => void }).__openCart = openCart;
    return () => {
      const cartWindow = window as unknown as { __openCart?: () => void };
      if (cartWindow.__openCart === openCart) delete cartWindow.__openCart;
    };
  }, []);


  if (isAdmin) {
    return (
      <div className="min-h-screen bg-[#F7F7F5]">
        <Outlet />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (bare) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <AppHeader onMenuClick={() => setMenuOpen(true)} onCartClick={() => setCartOpen(true)} />
        <main id="store-main"><Outlet /></main>
        <AppMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
        <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#F7F7F5]">
      {/* One storefront tree: banners, page body and footer mount exactly once.
          Only the header swaps at the lg breakpoint (both are always mounted,
          shown/hidden by CSS) so resizing never remounts the page. */}
      <div className="relative mx-auto flex w-full max-w-[430px] min-h-screen flex-col overflow-x-clip bg-white pb-32 md:max-w-none md:overflow-x-visible md:pb-0">
        {!isAccount && <><InauguralPriceBanner /><UrgencyStrip /></>}

        <div className="sticky top-0 z-30 lg:hidden">
          <AppHeader
            onMenuClick={() => setMenuOpen(true)}
            onCartClick={() => setCartOpen(true)}
          />
        </div>
        <div className="sticky top-0 z-30 hidden lg:block">
          <DesktopHeader
            onMenuClick={() => setMenuOpen(true)}
            onCartClick={() => setCartOpen(true)}
          />
        </div>

        <main id="store-main" className="min-w-0 md:flex-1">
          <Outlet />
        </main>

        <SiteFooter />
      </div>

      <BottomNav onCartClick={() => setCartOpen(true)} />
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
      <AppMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
      {/* <DeferredVita /> — hidden for now, keep the code */}
      <WhatsAppChatButton />
      {/* <VoiceAgent /> — disabled for now, keep the code */}
      <Toaster position="top-center" richColors />
    </div>
  );
}


function DesktopHeader({
  onMenuClick,
  onCartClick,
}: {
  onMenuClick: () => void;
  onCartClick: () => void;
}) {
  const cartCount = useCartStore((state) =>
    state.items.reduce((total, item) => total + item.quantity, 0),
  );

  const t = useT();
  const navItems: { to: string; key: TranslationKey }[] = [
    { to: "/shop", key: "nav.shop" },
    { to: "/quiz", key: "nav.quiz" },
    { to: "/ingredients", key: "nav.ingredients" },
    { to: "/blog", key: "nav.library" },
  ];
  return (
    <header data-site-header className="tw-header tw-header--desktop"><a href="#store-main" className="tw-skip-link">Skip to content</a>
      <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-8 px-8 py-4">
        <Link to="/" aria-label="TruWe" className="flex items-center shrink-0">
          <img src={truweLogo} alt="TruWe - Health and Wellness Supplements" width={960} height={209} className="h-[38px] w-auto" draggable={false} />
        </Link>
        <nav aria-label="Primary desktop" className="flex items-center gap-7">
          {navItems.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="text-[13px] font-medium tracking-tight text-[#2C2C2C]/75 transition-colors hover:text-[#1E5EFF]"
            >
              {t(n.key)}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <DownloadAppButton />
          <Link
            to="/account"
            aria-label={t("nav.account")}
            className="grid h-9 w-9 place-items-center rounded-full bg-[#F2F3F5] text-[#2C2C2C] transition-colors hover:bg-[#E8EAEE]"
          >
            <User className="h-4 w-4" strokeWidth={2} />
          </Link>
          <button
            type="button"
            onClick={onCartClick}
            aria-label={t("nav.openCart")}
            className="relative grid h-9 w-9 place-items-center rounded-full bg-[#F2F3F5] text-[#2C2C2C] transition-colors hover:bg-[#E8EAEE]"
          >
            <ShoppingCart className="h-4 w-4" strokeWidth={2} />
            {cartCount > 0 && (
              <span className="absolute -right-1 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-[#1E5EFF] px-1 text-[10px] font-bold text-white">
                {cartCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={onMenuClick}
            aria-label={t("nav.openMenu")}
            className="tw-header__icon"
          >
            <Menu className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </header>
  );
}



function AppHeader({
  onMenuClick,
  onCartClick,
}: {
  onMenuClick: () => void;
  onCartClick?: () => void;
}) {
  const t = useT();
  const cartCount = useCartStore((state) =>
    state.items.reduce((total, item) => total + item.quantity, 0),
  );
  return (
    <header data-site-header className="tw-header tw-header--mobile">
      <a href="#store-main" className="tw-skip-link">Skip to content</a>
      <div className="tw-header__actions">
        <Link to="/account" aria-label={t("nav.account")} className="tw-header__icon"><User size={19} strokeWidth={1.7} /></Link>
        <DownloadAppButton />
      </div>
      <Link to="/" aria-label="TruWe" className="tw-header__logo"><img src={truweLogo} alt="TruWe — Precision formulated" width={960} height={209} draggable={false} /></Link>
      <div className="tw-header__actions">
        {onCartClick && <button type="button" onClick={onCartClick} aria-label={t("nav.openCart")} className="tw-header__icon tw-header__cart"><ShoppingCart size={20} strokeWidth={1.7} />{cartCount > 0 && <span className="tw-header__count">{cartCount}</span>}</button>}
        <button type="button" onClick={onMenuClick} aria-label={t("nav.openMenu")} className="tw-header__icon"><Menu size={21} strokeWidth={1.7} /></button>
      </div>
    </header>
  );
}


const MENU_LINKS: { to: string; key: TranslationKey; subKey: TranslationKey; label: string; sub: string }[] = [
  { to: "/shop", key: "nav.shop", subKey: "menu.sub.shop", label: "Shop", sub: "All products" },
  { to: "/kids", key: "nav.kidsFull", subKey: "menu.sub.kids", label: "TruWe Kids", sub: "For little ones" },
  { to: "/quiz", key: "nav.quiz", subKey: "menu.sub.quiz", label: "Find your routine", sub: "Personalised quiz" },
  { to: "/diet-plan", key: "nav.dietPlan", subKey: "menu.sub.dietPlan", label: "Free diet plan", sub: "7-day plan & routine" },
  { to: "/ingredients", key: "nav.ingredients", subKey: "menu.sub.ingredients", label: "Ingredients", sub: "Science & research" },
  { to: "/blog", key: "nav.library", subKey: "menu.sub.library", label: "Library", sub: "Articles & guides" },
  { to: "/trucoins", key: "nav.loyalty", subKey: "menu.sub.loyalty", label: "Loyalty Rewards", sub: "Earn TruCoins" },
  { to: "/about", key: "nav.about", subKey: "menu.sub.about", label: "About", sub: "Our story" },
  { to: "/contact", key: "nav.contact", subKey: "menu.sub.contact", label: "Contact", sub: "We're here to help" },
];

type SearchItem = {
  to: string;
  /** English label — also the search-matching text. */
  label: string;
  sub?: string;
  /** Translation keys, when the row is UI navigation rather than a product. */
  labelKey?: TranslationKey;
  subKey?: TranslationKey;
  keywords: string;
  kind: "product" | "page";
};

const PRODUCT_ITEMS: SearchItem[] = [
  {
    to: "/products/mag-absorb-pro",
    label: "Mag Absorb Pro",
    sub: "Sleep · stress · recovery",
    keywords: "magnesium bisglycinate aquamin sleep stress recovery calm nerves",
    kind: "product",
  },
  {
    to: "/products/liver-biome-plus",
    label: "Liver Biome Plus",
    sub: "Liver · gut · detox",
    keywords: "liver gut detox curcumin silymarin nac ala probiotic bloating fatigue",
    kind: "product",
  },
  {
    to: "/products/ova-balance",
    label: "Ova Balance",
    sub: "PCOS · hormones · cycle",
    keywords: "pcos hormones women myo inositol d-chiro folate cycle skin mood",
    kind: "product",
  },
];

const PAGE_ITEMS: SearchItem[] = MENU_LINKS.map((m) => ({
  to: m.to,
  label: m.label,
  sub: m.sub,
  labelKey: m.key,
  subKey: m.subKey,
  keywords: `${m.label} ${m.sub ?? ""}`.toLowerCase(),
  kind: "page",
}));

const SEARCH_INDEX: SearchItem[] = [...PRODUCT_ITEMS, ...PAGE_ITEMS];

function AppMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Lock body scroll while menu is open + focus search
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        const focusable = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('a[href],button:not([disabled]),input:not([disabled]),[tabindex="0"]') ?? []).filter((node) => node.getClientRects().length > 0);
        const first = focusable[0]; const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => inputRef.current?.focus(), 280);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  // Reset query when the menu closes
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const q = query.trim().toLowerCase();
  const productHits = useMemo(
    () =>
      q
        ? PRODUCT_ITEMS.filter(
            (p) => p.label.toLowerCase().includes(q) || p.keywords.includes(q),
          )
        : PRODUCT_ITEMS,
    [q],
  );
  const pageHits = useMemo(
    () =>
      q
        ? PAGE_ITEMS.filter(
            (p) => p.label.toLowerCase().includes(q) || p.keywords.includes(q),
          )
        : PAGE_ITEMS,
    [q],
  );
  const hasResults = productHits.length + pageHits.length > 0;

  return (
    <div
      aria-hidden={!open}
      inert={!open}
      className={`tw-site-menu fixed inset-0 z-[60] ${open ? "pointer-events-auto" : "pointer-events-none"}`}
    >
      {/* Backdrop with iOS-style blur fade */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/30 backdrop-blur-md transition-opacity duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Panel, slides + scales in like a sheet on iOS */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("nav.menu")}
        className={`absolute inset-x-0 top-0 mx-auto w-full max-w-[430px] origin-top will-change-transform transition-all duration-[520ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open
            ? "translate-y-0 opacity-100 scale-100"
            : "-translate-y-3 opacity-0 scale-[0.985]"
        }`}
      >
        <div className="m-3 flex max-h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-[28px] bg-white/95 shadow-[0_30px_80px_-20px_rgba(43,45,66,0.35)] ring-1 ring-black/5 backdrop-blur-2xl">
          {/* Header row */}
          <div className="flex items-center justify-between border-b border-black/5 px-5 py-4">
            <span className="text-[15px] font-semibold tracking-tight text-[#2B2D42]">
              {t("nav.menu")}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("nav.closeMenu")}
              className="grid h-9 w-9 place-items-center rounded-full bg-[#F2F3F5] text-[#2B2D42] transition-transform active:scale-90"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>

          {/* Search field */}
          <div className="px-4 pt-3 pb-2">
            <label className="flex items-center gap-2 rounded-2xl bg-[#F2F3F5] px-3.5 py-2.5 ring-1 ring-transparent transition focus-within:bg-white focus-within:ring-[#2B2D42]/15">
              <Search className="h-4 w-4 text-[#6B7280]" strokeWidth={2} />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("search.placeholder")}
                aria-label={t("search.aria")}
                className="w-full bg-transparent text-[14px] tracking-tight text-[#2B2D42] placeholder:text-[#9AA1AC] focus:outline-none"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    inputRef.current?.focus();
                  }}
                  aria-label={t("search.clear")}
                  className="grid h-5 w-5 place-items-center rounded-full bg-[#D9D9D6] text-[#2B2D42] transition-transform active:scale-90"
                >
                  <X className="h-3 w-3" strokeWidth={2.5} />
                </button>
              ) : null}
            </label>
          </div>

          {/* Results */}
          <nav className="flex-1 overflow-y-auto p-2 pt-0">
            {!hasResults ? (
              <div className="px-4 py-10 text-center">
                <p className="text-[14px] font-medium tracking-tight text-[#2B2D42]">
                  {t("search.noMatches", { query })}
                </p>
                <p className="mt-1 text-[12px] text-[#6B7280]">
                  {t("search.tryHint")}
                </p>
              </div>
            ) : (
              <>
                {productHits.length > 0 ? (
                  <ResultSection title={t("search.products")} open={open}>
                    {productHits.map((item, idx) => (
                      <MenuRow
                        key={item.to}
                        item={item}
                        idx={idx}
                        open={open}
                        onClose={onClose}
                      />
                    ))}
                  </ResultSection>
                ) : null}

                {pageHits.length > 0 ? (
                  <ResultSection title={t("search.pages")} open={open}>
                    {pageHits.map((item, idx) => (
                      <MenuRow
                        key={item.to}
                        item={item}
                        idx={idx + productHits.length}
                        open={open}
                        onClose={onClose}
                      />
                    ))}
                  </ResultSection>
                ) : null}
              </>
            )}
          </nav>

          <div className="tw-menu__utilities">
            <Link to="/account" onClick={onClose}><User size={18} />{t("nav.account")}</Link>
            <LanguageToggle />
            <DownloadAppButton />
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultSection({
  title,
  open,
  children,
}: {
  title: string;
  open: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="px-1 pt-2">
      <div
        className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9AA1AC]"
        style={{
          opacity: open ? 1 : 0,
          transition: "opacity 280ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {title}
      </div>
      <ul>{children}</ul>
    </div>
  );
}

function MenuRow({
  item,
  idx,
  open,
  onClose,
}: {
  item: SearchItem;
  idx: number;
  open: boolean;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <li>
      <Link
        to={item.to}
        onClick={onClose}
        className="group flex items-center justify-between rounded-2xl px-4 py-3.5 transition-colors hover:bg-[#F6F7F9] active:bg-[#EEF0F3]"
        style={{
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0)" : "translateY(8px)",
          transition: `opacity 360ms cubic-bezier(0.22,1,0.36,1) ${
            open ? 80 + idx * 35 : 0
          }ms, transform 420ms cubic-bezier(0.22,1,0.36,1) ${
            open ? 80 + idx * 35 : 0
          }ms`,
        }}
      >
        <span className="flex flex-col">
          <span className="text-[15px] font-semibold tracking-tight text-[#2B2D42]">
            {/* Product names are trademarks — never translated. */}
            {item.labelKey ? t(item.labelKey) : item.label}
          </span>
          {item.sub ? (
            <span className="mt-0.5 text-[12px] text-[#6B7280]">
              {item.subKey ? t(item.subKey) : item.sub}
            </span>
          ) : null}
        </span>
        <span
          aria-hidden
          className="text-[#9AA1AC] transition-transform duration-300 group-hover:translate-x-0.5"
        >
          ›
        </span>
      </Link>
    </li>
  );
}

